import { pipe } from 'it-pipe';
import { pack } from 'it-tar';

/** One file to place in the tar archive. */
export interface TarEntry {
  name: string;
  bytes: Uint8Array;
}

/**
 * Packs the given entries into a single uncompressed (USTAR) tar archive and
 * returns it as one `Uint8Array`. No gzip/deflate is applied — this is a plain
 * tarball, matching the "tarred but not compressed" requirement of the
 * bulk-file-key upload flow.
 */
export async function packTar(entries: TarEntry[]): Promise<Uint8Array<ArrayBuffer>> {
  const chunks: Uint8Array[] = [];

  await pipe(
    entries.map((entry) => ({
      header: { name: entry.name, size: entry.bytes.length },
      body: entry.bytes,
    })),
    pack(),
    async (source) => {
      for await (const chunk of source) {
        chunks.push(chunk);
      }
    }
  );

  // Concatenated by hand (not uint8arrays' concat) so the result is typed as
  // ArrayBuffer-backed, which `File`/`Blob` construction requires.
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const tarBytes = new Uint8Array(totalLength);
  let offset = 0;
  for (const chunk of chunks) {
    tarBytes.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return tarBytes;
}
